import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import HandymanIcon from '@mui/icons-material/Handyman';
import HardwareIcon from '@mui/icons-material/Hardware';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import RefreshIcon from '@mui/icons-material/Refresh';
import TodayIcon from '@mui/icons-material/Today';
import BuildIcon from '@mui/icons-material/Build';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { useAuth } from '../../auth/AuthContext';
import { useWhoAmI } from '../../auth/AdminGate';
import {
  ApiError,
  IngestOutcome,
  ItemSearchHit,
  RepairOutcome,
  RepairReport,
  SweepOutcome,
  Transaction,
  TransactionLineItem,
  TransactionsIngestReport,
  ingestTransactions,
  listTransactions,
  reconcileLineItem,
  repairTransactions,
  sweepTransactions,
} from '../../api';
import Price from '../widgets/Price';
import ItemPickerDialog from '../widgets/transactions/ItemPickerDialog';
const fmtTime = (s: string | null | undefined) => {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return s;
  }
};

// `YYYY-MM-DD` for today in the local timezone. The backend interprets
// the value as a UTC midnight-to-midnight window — close enough for an
// admin tool used by people in one timezone; revisit if the day boundary
// disagreement starts mattering.
const isoLocalDay = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const parseLocalDay = (s: string): Date => {
  const [y, m, d] = s.split('-').map((p) => parseInt(p, 10));
  return new Date(y, (m ?? 1) - 1, d ?? 1);
};

const shiftDay = (iso: string, deltaDays: number): string => {
  const d = parseLocalDay(iso);
  d.setDate(d.getDate() + deltaDays);
  return isoLocalDay(d);
};

// Friendly tenant-aware location label. Mirrors tqos-admin's hand-rolled
// switch — a tiny lookup table is fine here; revisit when we have a real
// locations registry. Matches `backroom-mongodb::stock_location_name`.
const LOCATION_LABELS: Record<string, string> = {
  LXMEN9FKB39PR: 'Back Room (Liverpool)',
  L2DE0WD9X640G: 'Bridgewater Front',
  L49GEDBB88QHR: 'Bong Bus',
  '0000035a-a585-9cc5-05b3-111ea186b59b': 'Florist (Liverpool)',
  '00000375-7dfb-8eac-c871-97e0b9adc3f7': 'Bridgewater Back',
};
const locationLabel = (vendor: string, location: string) =>
  LOCATION_LABELS[location] ?? `${vendor} · ${location.slice(0, 8)}`;

type SweepState =
  | { kind: 'idle' }
  | { kind: 'running' }
  | { kind: 'busy' }            // another caller is sweeping; we backed off
  | { kind: 'done'; outcome: Extract<SweepOutcome, { kind: 'ok' }>['report'] }
  | { kind: 'error'; message: string };

const TransactionsPage: React.FC = () => {
  const { idToken } = useAuth();
  const me = useWhoAmI();
  const isGod = me.roles.includes('god');
  const [day, setDay] = useState<string>(() => isoLocalDay(new Date()));

  const [data, setData] = useState<Transaction[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [sweep, setSweep] = useState<SweepState>({ kind: 'idle' });
  const [repairOpen, setRepairOpen] = useState(false);
  const [ingestOpen, setIngestOpen] = useState(false);
  // Bump on sweep success to force the day-listing effect to re-run, so
  // any auto-reconciliations the sweep performed show up immediately.
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    if (!idToken) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    listTransactions(idToken, day)
      .then((rows) => {
        if (!cancelled) setData(rows);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(
          err instanceof ApiError
            ? `API error ${err.status}`
            : (err as Error)?.message || 'fetch failed',
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [idToken, day, reloadTick]);

  // The reconcile sweep — invoked once on mount, and again whenever the
  // user clicks the refresh button. Bumps `reloadTick` on success so the
  // day-listing effect re-runs and any auto-reconciliations appear.
  const runSweep = useCallback(async () => {
    if (!idToken) return;
    setSweep({ kind: 'running' });
    try {
      const outcome = await sweepTransactions(idToken);
      switch (outcome.kind) {
        case 'ok':
          setSweep({ kind: 'done', outcome: outcome.report });
          setReloadTick((n) => n + 1);
          break;
        case 'busy':
          setSweep({ kind: 'busy' });
          break;
        case 'not_configured':
          setSweep({
            kind: 'error',
            message: 'Mongo not configured on the server.',
          });
          break;
        case 'error':
          setSweep({ kind: 'error', message: `Sweep failed (${outcome.status})` });
          break;
      }
    } catch (err) {
      setSweep({
        kind: 'error',
        message: (err as Error)?.message || 'sweep failed',
      });
    }
  }, [idToken]);

  // Fire the auto-reconcile sweep once on mount. Doesn't block the
  // initial transaction load — the user sees data immediately and the
  // list re-fetches when the sweep finishes (via reloadTick). We
  // deliberately do NOT re-fire on day-change — sweeps run against
  // recent activity, not the day being viewed.
  useEffect(() => {
    runSweep();
  }, [runSweep]);

  // Net of refunds. A day's takings are what was rung up minus what
  // went back out; showing gross alone overstates every day that had a
  // return. Gross stays visible next to it so a refund-heavy day reads
  // as "busy with returns" rather than just "quiet".
  const totals = useMemo(() => {
    let subtotal = 0;
    let total = 0;
    let refundedSubtotal = 0;
    let refundedTotal = 0;
    // Owner withdrawals took no money, so neither their cost nor their
    // HST belongs in a day's figures — they'll be reported on
    // separately. The rows stay listed and flagged; only the arithmetic
    // ignores them.
    (data ?? []).forEach((t) => {
      if (t.price_basis === 'owner') return;
      subtotal += t.subtotal ?? 0;
      total += t.total ?? 0;
      refundedSubtotal += t.refunded_subtotal ?? 0;
      refundedTotal += t.refunded_total ?? 0;
    });
    return {
      subtotal,
      total,
      refundedSubtotal,
      refundedTotal,
      netSubtotal: subtotal - refundedSubtotal,
      netTotal: total - refundedTotal,
      count: data?.length ?? 0,
    };
  }, [data]);

  const replaceTxnInPlace = (vendor: string, location: string, txnId: string, next: Transaction) => {
    setData((prev) =>
      prev
        ? prev.map((t) =>
            t.identifier.vendor === vendor &&
            t.identifier.location === location &&
            t.identifier.id === txnId
              ? next
              : t,
          )
        : prev,
    );
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, color: '#1f2937' }}>
        Transactions
      </Typography>
      <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
        Cross-tenant in-store sales. Reconcile line items against the catalog
        when an item isn't recognized.
      </Typography>

      <SweepBanner state={sweep} />


      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Tooltip title="Previous day">
          <span>
            <IconButton
              size="small"
              onClick={() => setDay(shiftDay(day, -1))}
              disabled={loading}
            >
              <ChevronLeftIcon />
            </IconButton>
          </span>
        </Tooltip>
        <TextField
          type="date"
          size="small"
          value={day}
          onChange={(e) => setDay(e.target.value)}
          inputProps={{ max: isoLocalDay(new Date()) }}
          sx={{ minWidth: 170 }}
        />
        <Tooltip title="Next day">
          <span>
            <IconButton
              size="small"
              onClick={() => setDay(shiftDay(day, 1))}
              disabled={loading || day >= isoLocalDay(new Date())}
            >
              <ChevronRightIcon />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Today">
          <span>
            <IconButton
              size="small"
              onClick={() => setDay(isoLocalDay(new Date()))}
              disabled={loading || day === isoLocalDay(new Date())}
            >
              <TodayIcon />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Re-run reconciliation sweep">
          <span>
            <IconButton
              size="small"
              onClick={runSweep}
              disabled={sweep.kind === 'running'}
            >
              {sweep.kind === 'running' ? (
                <CircularProgress size={18} />
              ) : (
                <RefreshIcon />
              )}
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Pull newly-closed transactions from Square">
          <span>
            <IconButton size="small" onClick={() => setIngestOpen(true)}>
              <CloudDownloadIcon />
            </IconButton>
          </span>
        </Tooltip>
        {isGod && (
          <Tooltip title="Repair transactions damaged by the legacy sweep bug">
            <span>
              <IconButton size="small" onClick={() => setRepairOpen(true)}>
                <BuildIcon />
              </IconButton>
            </span>
          </Tooltip>
        )}

        <Box sx={{ flex: 1 }} />

        <Typography variant="body2" sx={{ color: '#64748b' }}>
          {totals.count} txn{totals.count === 1 ? '' : 's'} · subtotal{' '}
          <strong><Price price={totals.netSubtotal} showZero /></strong> · total{' '}
          <strong><Price price={totals.netTotal} showZero /></strong>
          {totals.refundedTotal > 0 && (
            <>
              {' · '}
              <span style={{ color: '#b91c1c' }}>
                less <Price price={totals.refundedTotal} showZero /> refunded
              </span>
              {' (gross '}
              <Price price={totals.total} showZero />
              {')'}
            </>
          )}

        </Typography>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{ border: '1px solid #e5e7eb', borderRadius: 1 }}
      >
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#fafafa' }}>
              <TableCell sx={{ width: 40 }} />
              <TableCell sx={{ fontWeight: 600 }}>Location</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Time</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>State</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Reconciled</TableCell>
              <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>Subtotal</TableCell>
              <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && data === null ? (
              <LoadingRow />
            ) : (data?.length ?? 0) === 0 ? (
              <EmptyRow text="No transactions on this day." />
            ) : (
              data!.map((t, i) => (
                <TransactionRow
                  key={`${t.identifier.vendor}-${t.identifier.location}-${t.identifier.id}-${i}`}
                  txn={t}
                  onReconciled={(next) =>
                    replaceTxnInPlace(
                      t.identifier.vendor,
                      t.identifier.location,
                      t.identifier.id,
                      next,
                    )
                  }
                />
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {isGod && (
        <RepairDialog
          open={repairOpen}
          onClose={() => setRepairOpen(false)}
          onRepaired={() => setReloadTick((n) => n + 1)}
        />
      )}
      <IngestDialog
        open={ingestOpen}
        onClose={() => setIngestOpen(false)}
        onIngested={() => setReloadTick((n) => n + 1)}
      />
    </Box>
  );
};

// Status banner for the auto-reconcile sweep. Shows a small live update
// at the top of the page; hides itself once the sweep finishes cleanly
// with nothing interesting to report. Failures stay visible so the
// admin doesn't miss them.
const SweepBanner: React.FC<{ state: SweepState }> = ({ state }) => {
  if (state.kind === 'idle') return null;

  if (state.kind === 'running') {
    return (
      <Box
        sx={{
          mb: 2,
          p: 1.25,
          border: '1px solid #cbd5e1',
          borderRadius: 1,
          backgroundColor: '#f8fafc',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <CircularProgress size={16} />
        <Typography
          variant="body2"
          sx={{ color: '#475569', fontStyle: 'italic' }}
        >
          Reconciling recent transactions…
        </Typography>
      </Box>
    );
  }

  if (state.kind === 'busy') {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        Another reconcile is already running. Page will pick up its results
        on next refresh.
      </Alert>
    );
  }

  if (state.kind === 'error') {
    return (
      <Alert severity="warning" sx={{ mb: 2 }}>
        Couldn't run auto-reconcile: {state.message}. Line items will still
        appear; manual force-reconcile still works.
      </Alert>
    );
  }

  // 'done' — only show a banner when there's something interesting to
  // report; quiet success (0 reconciled, 0 decremented) hides itself
  // after a moment by simply not rendering.
  const { reconciled, decremented, cant_reconcile } = state.outcome;
  if (reconciled === 0 && decremented === 0 && cant_reconcile === 0) {
    return null;
  }
  return (
    <Alert severity="success" sx={{ mb: 2 }}>
      Auto-reconcile complete: {reconciled} reconciled, {decremented} stock
      updated{cant_reconcile > 0
        ? `, ${cant_reconcile} need manual review`
        : ''}.
    </Alert>
  );
};

// One-shot repair for transactions damaged by the legacy sweep bug. The
// default start date is the first day the bug shipped — operator can dial
// it forward if they only want to retry a recent window, but should not
// dial it back further (no damage existed before that day).
const REPAIR_DEFAULT_FROM = '2026-05-15';

const RepairDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onRepaired: () => void;
}> = ({ open, onClose, onRepaired }) => {
  const { idToken } = useAuth();
  const [from, setFrom] = useState<string>(REPAIR_DEFAULT_FROM);
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<RepairOutcome | null>(null);

  const reset = () => {
    setFrom(REPAIR_DEFAULT_FROM);
    setBusy(false);
    setOutcome(null);
  };

  const handleClose = () => {
    if (busy) return;
    reset();
    onClose();
  };

  const run = async () => {
    if (!idToken) return;
    setBusy(true);
    setOutcome(null);
    try {
      const result = await repairTransactions(idToken, from);
      setOutcome(result);
      if (result.kind === 'ok' && result.report.repaired > 0) {
        onRepaired();
      }
    } catch (e) {
      setOutcome({ kind: 'error', status: 0 });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Repair damaged transactions</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
          Re-fetches affected orders from Square and rebuilds the line items
          the legacy sweep destroyed. Existing reconciliation data is
          preserved. Safe to re-run.
        </Typography>
        <TextField
          type="date"
          label="From (inclusive)"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          size="small"
          fullWidth
          disabled={busy}
          inputProps={{ max: isoLocalDay(new Date()) }}
        />
        {outcome && <RepairOutcomeView outcome={outcome} />}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={busy} sx={{ textTransform: 'none' }}>
          Close
        </Button>
        <Button
          onClick={run}
          variant="contained"
          disabled={busy || !from}
          sx={{ textTransform: 'none' }}
        >
          {busy ? 'Running…' : 'Run repair'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const RepairOutcomeView: React.FC<{ outcome: RepairOutcome }> = ({ outcome }) => {
  if (outcome.kind === 'not_configured') {
    return (
      <Alert severity="warning" sx={{ mt: 2 }}>
        Mongo or Square isn't configured on the server.
      </Alert>
    );
  }
  if (outcome.kind === 'bad_date') {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Couldn't parse the start date.
      </Alert>
    );
  }
  if (outcome.kind === 'error') {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Repair failed (HTTP {outcome.status || '—'}).
      </Alert>
    );
  }
  return <RepairReportView report={outcome.report} />;
};

const RepairReportView: React.FC<{ report: RepairReport }> = ({ report }) => {
  if (report.scanned === 0) {
    return (
      <Alert severity="success" sx={{ mt: 2 }}>
        No damaged transactions found.
      </Alert>
    );
  }
  const severity: 'success' | 'warning' =
    report.failed === 0 && report.skipped === 0 ? 'success' : 'warning';
  return (
    <>
      <Alert severity={severity} sx={{ mt: 2 }}>
        Scanned {report.scanned}, repaired {report.repaired}, skipped{' '}
        {report.skipped}, failed {report.failed} in {report.elapsed_ms} ms.
      </Alert>
      {report.details.length > 0 && (
        <Box sx={{ mt: 2, maxHeight: 240, overflow: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Txn</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Outcome</TableCell>
                <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>
                  Repaired
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Detail</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {report.details.map((d) => (
                <TableRow key={d.txn_id}>
                  <TableCell
                    sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                  >
                    {d.txn_id.slice(0, 10)}…
                  </TableCell>
                  <TableCell>{d.outcome}</TableCell>
                  <TableCell sx={{ textAlign: 'right' }}>{d.repaired}</TableCell>
                  <TableCell sx={{ fontSize: '0.8rem', color: '#64748b' }}>
                    {d.message ?? ''}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </>
  );
};

// Manual trigger for the Square→Mongo transactions ingester. Server uses
// the same 30-min lookback the background tick does, so this is just
// "skip the 5-min wait" — backfills outside that window go through the
// CLI. Tap dispatches the request; the dialog shows the report (or the
// failure mode) and closes when the operator dismisses it.
const IngestDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onIngested: () => void;
}> = ({ open, onClose, onIngested }) => {
  const { idToken } = useAuth();
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<IngestOutcome | null>(null);

  const reset = () => {
    setBusy(false);
    setOutcome(null);
  };

  const handleClose = () => {
    if (busy) return;
    reset();
    onClose();
  };

  const run = async () => {
    if (!idToken) return;
    setBusy(true);
    setOutcome(null);
    try {
      const result = await ingestTransactions(idToken);
      setOutcome(result);
      // Only force the day-listing to reload when we actually inserted
      // something. Re-fetching after a quiet tick (everything already
      // present) would just thrash the table for no visual change.
      if (result.kind === 'ok' && result.report.inserted > 0) {
        onIngested();
      }
    } catch {
      setOutcome({ kind: 'error', status: 0 });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Ingest from Square</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
          Pull recently-closed Square orders into the transactions
          collection. Server walks the last 30 minutes by `closed_at`;
          orders already in Mongo are skipped atomically. Safe to re-run
          — the background tick runs the same query every 5 minutes.
        </Typography>
        {outcome && <IngestOutcomeView outcome={outcome} />}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={busy} sx={{ textTransform: 'none' }}>
          Close
        </Button>
        <Button
          onClick={run}
          variant="contained"
          disabled={busy}
          sx={{ textTransform: 'none' }}
        >
          {busy ? 'Ingesting…' : 'Run ingest'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const IngestOutcomeView: React.FC<{ outcome: IngestOutcome }> = ({ outcome }) => {
  if (outcome.kind === 'not_configured') {
    return (
      <Alert severity="warning" sx={{ mt: 2 }}>
        Mongo or Square isn't configured on the server.
      </Alert>
    );
  }
  if (outcome.kind === 'busy') {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        Another ingest is already running. The background tick will pick
        up the same window — try again in a moment.
      </Alert>
    );
  }
  if (outcome.kind === 'square_error') {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Couldn't reach Square. Try again — the background tick will retry
        on its next firing regardless.
      </Alert>
    );
  }
  if (outcome.kind === 'error') {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Ingest failed (HTTP {outcome.status || '—'}).
      </Alert>
    );
  }
  return <IngestReportView report={outcome.report} />;
};

const IngestReportView: React.FC<{ report: TransactionsIngestReport }> = ({
  report,
}) => {
  // "Quiet success" = the steady state once the tick has been running a
  // while; surface as a soft info, not a warning. New inserts get the
  // success colour so it's easy to spot when a fresh order landed.
  const severity: 'success' | 'info' = report.inserted > 0 ? 'success' : 'info';
  return (
    <Alert severity={severity} sx={{ mt: 2 }}>
      Fetched {report.fetched}, inserted {report.inserted}, already
      present {report.already_present}, skipped {report.skipped} (
      {report.pages} page{report.pages === 1 ? '' : 's'}) in{' '}
      {report.elapsed_ms} ms.
    </Alert>
  );
};

const LoadingRow: React.FC = () => (
  <TableRow>
    <TableCell colSpan={7} sx={{ textAlign: 'center', py: 6, borderBottom: 'none' }}>
      <CircularProgress size={20} />
    </TableCell>
  </TableRow>
);

const EmptyRow: React.FC<{ text: string }> = ({ text }) => (
  <TableRow>
    <TableCell
      colSpan={7}
      sx={{
        textAlign: 'center',
        color: '#94a3b8',
        py: 6,
        fontStyle: 'italic',
        borderBottom: 'none',
      }}
    >
      {text}
    </TableCell>
  </TableRow>
);

const lineItemIsReconciled = (i: TransactionLineItem) => !!i.reconciliation?.user;

// Warm wash for staff purchases. Same family as the parent-item
// treatment on the items grid rather than a new colour: orange reads as
// "noteworthy, not wrong", which is exactly what an employee sale is.
const EMPLOYEE_BG = '#fff7ed';
const EMPLOYEE_BORDER = '#fed7aa';
const EMPLOYEE_TEXT = '#9a3412';

// Owner withdrawals: stock off the shelf with no money and no tax, and
// excluded from every revenue figure. Red because it's a different kind
// of event from a staff discount, not a louder version of one.
const OWNER_BG = '#fef2f2';
const OWNER_BORDER = '#fca5a5';
const OWNER_TEXT = '#991b1b';

const TransactionRow: React.FC<{
  txn: Transaction;
  onReconciled: (next: Transaction) => void;
}> = ({ txn, onReconciled }) => {
  const [open, setOpen] = useState(false);
  // Refund state for the whole sale, from the stored refund rows.
  const refundedSubtotal = txn.refunded_subtotal ?? 0;
  const refundedTotal = txn.refunded_total ?? 0;
  const refundedLines = txn.line_items.filter((l) => (l.refunded_quantity ?? 0) > 0);
  const fullyRefundedLines = txn.line_items.filter(
    (l) => (l.refunded_quantity ?? 0) > 0 && (l.refundable_quantity ?? 0) <= 0,
  );
  // "Full" only when every line went back — a sale with one line
  // returned out of three is partial, however much money that was.
  const refundState: 'none' | 'part' | 'full' =
    refundedLines.length === 0
      ? 'none'
      : fullyRefundedLines.length === txn.line_items.length
        ? 'full'
        : 'part';

  // Employee (staff-priced) sales. Square rows carry no price basis and
  // are retail by definition, so only an explicit 'employee' counts.
  const employeeSale = txn.price_basis === 'employee';
  const ownerWithdrawal = txn.price_basis === 'owner';

  const reconciledCount = txn.line_items.filter(lineItemIsReconciled).length;
  const fullyReconciled =
    txn.line_items.length > 0 && reconciledCount === txn.line_items.length;

  return (
    <>
      <TableRow
        sx={{
          '& > *': { borderBottom: 'unset' },
          // Staff purchases get a warm wash so they're separable at a
          // glance when scanning a day. The chip below carries the same
          // meaning in text — colour alone would be lost on a printout
          // or to a colour-blind reader.
          ...(ownerWithdrawal
            ? {
                backgroundColor: OWNER_BG,
                '& > td:first-of-type': { borderLeft: `3px solid ${OWNER_BORDER}` },
              }
            : employeeSale
              ? {
                  backgroundColor: EMPLOYEE_BG,
                  '& > td:first-of-type': { borderLeft: `3px solid ${EMPLOYEE_BORDER}` },
                }
              : {}),
        }}
      >
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          {locationLabel(txn.identifier.vendor, txn.identifier.location)}
        </TableCell>
        <TableCell>{fmtTime(txn.created_at)}</TableCell>
        <TableCell>
          {txn.state ?? txn.status ?? '—'}
          {employeeSale && (
            <Chip
              size="small"
              label="Employee"
              sx={{
                ml: 0.75,
                height: 20,
                fontSize: '0.7rem',
                backgroundColor: EMPLOYEE_BG,
                color: EMPLOYEE_TEXT,
                border: `1px solid ${EMPLOYEE_BORDER}`,
              }}
            />
          )}
          {ownerWithdrawal && (
            <Chip
              size="small"
              label="Owner"
              sx={{
                ml: 0.75,
                height: 20,
                fontSize: '0.7rem',
                backgroundColor: '#fff',
                color: OWNER_TEXT,
                border: `1px solid ${OWNER_BORDER}`,
              }}
            />
          )}
          {refundState !== 'none' && (
            <Chip
              size="small"
              label={refundState === 'full' ? 'Refunded' : 'Part refunded'}
              sx={{
                ml: 0.75,
                height: 20,
                fontSize: '0.7rem',
                backgroundColor: '#fef2f2',
                color: '#b91c1c',
                border: '1px solid #fecaca',
              }}
            />
          )}
        </TableCell>
        <TableCell>
          {fullyReconciled ? (
            <CheckIcon sx={{ color: '#15803d' }} fontSize="small" />
          ) : (
            <Typography
              variant="caption"
              sx={{ color: '#94a3b8' }}
            >{`${reconciledCount}/${txn.line_items.length}`}</Typography>
          )}
        </TableCell>
        <TableCell sx={{ textAlign: 'right' }}>
          {refundedSubtotal > 0 ? (
            <>
              <Box sx={{ textDecoration: 'line-through', color: '#94a3b8' }}>
                <Price price={txn.subtotal} showZero />
              </Box>
              <Box>
                <Price price={(txn.subtotal ?? 0) - refundedSubtotal} showZero />
              </Box>
            </>
          ) : (
            <Price price={txn.subtotal} showZero />
          )}
        </TableCell>
        <TableCell sx={{ textAlign: 'right' }}>
          {refundedTotal > 0 ? (
            <>
              <Box sx={{ textDecoration: 'line-through', color: '#94a3b8' }}>
                <Price price={txn.total} showZero />
              </Box>
              <Box sx={{ fontWeight: 600 }}>
                <Price price={(txn.total ?? 0) - refundedTotal} showZero />
              </Box>
            </>
          ) : (
            <Price price={txn.total} showZero />
          )}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={7} sx={{ p: 0, borderBottom: open ? '1px solid #e5e7eb' : 'unset' }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ p: 2, backgroundColor: '#fafafa' }}>
              {(txn.cashier || txn.price_basis) && (
                <Typography
                  variant="caption"
                  sx={{ display: 'block', color: '#64748b', mb: 1 }}
                >
                  {txn.cashier ? `Rang up by ${txn.cashier}` : 'Register sale'}
                  {txn.price_basis
                    ? ` · ${txn.price_basis === 'employee' ? 'employee pricing (at cost)' : 'retail pricing'}`
                    : ''}
                </Typography>
              )}
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Line items
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>Qty</TableCell>
                    <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>ea.</TableCell>
                    <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>Line</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Reconciled</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {txn.line_items.map((li, idx) => (
                    <LineItemRow
                      key={`${li.id ?? idx}`}
                      txn={txn}
                      lineItem={li}
                      onReconciled={(updated) => {
                        const next: Transaction = {
                          ...txn,
                          line_items: txn.line_items.map((x, i) =>
                            i === idx ? { ...x, reconciliation: updated } : x,
                          ),
                        };
                        onReconciled(next);
                      }}
                    />
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const LineItemRow: React.FC<{
  txn: Transaction;
  lineItem: TransactionLineItem;
  onReconciled: (r: TransactionLineItem['reconciliation']) => void;
}> = ({ txn, lineItem, onReconciled }) => {
  const { idToken } = useAuth();
  const [forceOpen, setForceOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string>('');
  const [pickerErr, setPickerErr] = useState<string>('');

  const qty = lineItem.quantity ?? 1;
  const price = lineItem.price ?? 0;
  const reconciled = lineItemIsReconciled(lineItem);
  // Refund state per line, same vocabulary the register uses: a fully
  // refunded line is struck through, a partial one says how much went
  // back. `refunded_quantity` is the ledger's answer; the legacy
  // `refunded` boolean on Square rows is hardcoded false and means
  // nothing.
  const refundedQty = lineItem.refunded_quantity ?? 0;
  const remaining = lineItem.refundable_quantity ?? qty;
  const fullyRefunded = refundedQty > 0 && remaining <= 0;
  const partlyRefunded = refundedQty > 0 && !fullyRefunded;
  const display =
    lineItem.reconciliation?.name || lineItem.name || lineItem.itemId || '(unknown)';

  const submitForce = async () => {
    if (!idToken || !lineItem.id) return;
    if (!name.trim()) {
      setErr('Enter a name.');
      return;
    }
    setBusy(true);
    setErr('');
    try {
      await reconcileLineItem(
        idToken,
        txn.identifier.vendor,
        txn.identifier.location,
        txn.identifier.id,
        lineItem.id,
        { name: name.trim(), itemId: '' },
      );
      // Server doesn't echo back the updated doc; reconstruct locally so
      // the row flips to "reconciled" without re-fetching the whole day.
      onReconciled({
        user: '(you)',
        location: txn.identifier.vendor,
        name: name.trim(),
        itemId: '',
      });
      setForceOpen(false);
      setName('');
    } catch (e: any) {
      setErr(
        e instanceof ApiError
          ? `API error ${e.status}`
          : (e as Error)?.message || 'reconcile failed',
      );
    } finally {
      setBusy(false);
    }
  };

  // Catalog-picker reconcile — the wrench path. Same wire shape as the
  // hammer path, but with `itemId` + `dbName` populated and the name
  // built from `{brand} > {name}` (matches the tqos-admin convention).
  const submitPick = async (hit: ItemSearchHit) => {
    if (!idToken || !lineItem.id) return;
    const displayName = hit.brand ? `${hit.brand} > ${hit.name}` : hit.name;
    setPickerErr('');
    try {
      await reconcileLineItem(
        idToken,
        txn.identifier.vendor,
        txn.identifier.location,
        txn.identifier.id,
        lineItem.id,
        { name: displayName, itemId: hit.id, dbName: hit.db_name },
      );
      onReconciled({
        user: '(you)',
        location: txn.identifier.vendor,
        name: displayName,
        itemId: hit.id,
        dbName: hit.db_name,
      });
      setPickerOpen(false);
    } catch (e: any) {
      setPickerErr(
        e instanceof ApiError
          ? `API error ${e.status}`
          : (e as Error)?.message || 'reconcile failed',
      );
    }
  };

  return (
    <>
      <TableRow>
        <TableCell>
          <Box
            sx={{
              textDecoration: fullyRefunded ? 'line-through' : 'none',
              color: fullyRefunded ? '#94a3b8' : 'inherit',
            }}
          >
            {display}
          </Box>
          {fullyRefunded && (
            <Chip
              size="small"
              label="Refunded"
              sx={{
                mt: 0.5,
                height: 18,
                fontSize: '0.68rem',
                backgroundColor: '#fef2f2',
                color: '#b91c1c',
                border: '1px solid #fecaca',
              }}
            />
          )}
          {partlyRefunded && (
            <Typography variant="caption" sx={{ display: 'block', color: '#b91c1c' }}>
              {refundedQty} of {qty} refunded
            </Typography>
          )}
          {lineItem.itemId && (
            <Box sx={{ color: '#94a3b8', fontSize: '0.75rem', fontFamily: 'monospace' }}>
              {lineItem.itemId}
            </Box>
          )}
        </TableCell>
        <TableCell sx={{ textAlign: 'right' }}>{qty}</TableCell>
        <TableCell sx={{ textAlign: 'right' }}><Price price={price} showZero /></TableCell>
        <TableCell sx={{ textAlign: 'right' }}>
          {refundedQty > 0 ? (
            <>
              <Box sx={{ textDecoration: 'line-through', color: '#94a3b8' }}>
                <Price price={price * qty} showZero />
              </Box>
              <Box>
                <Price price={price * remaining} showZero />
              </Box>
            </>
          ) : (
            <Price price={price * qty} showZero />
          )}
        </TableCell>
        <TableCell>
          {reconciled ? <CheckIcon fontSize="small" sx={{ color: '#15803d' }} /> : null}
        </TableCell>
        <TableCell>
          {!reconciled && lineItem.id && (
            <>
              <Tooltip title="Reconcile against a catalog item">
                <IconButton size="small" onClick={() => setPickerOpen(true)}>
                  <HandymanIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Force reconcile (assign a custom name; no catalog item)">
                <IconButton size="small" onClick={() => setForceOpen(true)}>
                  <HardwareIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
          {pickerErr && (
            <Typography variant="caption" sx={{ color: '#b91c1c', display: 'block' }}>
              {pickerErr}
            </Typography>
          )}
        </TableCell>
      </TableRow>

      <ItemPickerDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={submitPick}
        priceHint={price > 0 ? price : null}
      />

      <Dialog open={forceOpen} onClose={() => !busy && setForceOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Force reconcile</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            Use this when the line item doesn't correspond to anything in the
            catalog. Enter a friendly name that explains what was sold.
          </Typography>
          <TextField
            label="Custom item name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            size="small"
            autoFocus
            disabled={busy}
          />
          {err && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {err}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setForceOpen(false)}
            disabled={busy}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={submitForce}
            variant="contained"
            disabled={busy || !name.trim()}
            sx={{ textTransform: 'none' }}
          >
            {busy ? 'Saving…' : 'Reconcile'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TransactionsPage;
